import { Client } from "@/Common/Api/Client";
import { EligibilityPatientDemographicInformation } from "@/Common/Models/Patient/HealthInsuranceEligibility/EligibilityPatientDemographicInformation";
import { HealthInsuranceEligibility } from "@/Common/Models/Patient/HealthInsuranceEligibility/HealthInsuranceEligibility";
import { HealthInsuranceEligibilityStatus } from "@/Common/Models/Patient/HealthInsuranceEligibility/HealthInsuranceEligibilityStatus";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";
import { ClientResponse } from "../ClientResponse";
import { ErrorMessage } from "../Dto/ErrorMessage";
import { HealthInsuranceEligibilityResponse } from "./Dto/Response/HealthInsuranceEligibilityResponse";

class HealthInsuranceEligibilityClient {
    public static async getHealthInsuranceEligibility(
        personalHealthNumber?: string,
        dateOfBirth?: DateOnly,
        patientHealthCardProvinceLookupId?: string,
        maxAttemptsCount: number = 3
    ): Promise<HealthInsuranceEligibility> {
        return await this.executeQueryWithRetry(
            async () => await this.sendEligibilityRequest(personalHealthNumber, dateOfBirth, patientHealthCardProvinceLookupId),
            this.handleResponse,
            this.handleError,
            maxAttemptsCount
        );
    }

    private static async executeQueryWithRetry(
        sendRequest: () => Promise<ClientResponse<HealthInsuranceEligibilityResponse>>,
        handleResponse: (response: ClientResponse<HealthInsuranceEligibilityResponse>) => Promise<HealthInsuranceEligibility>,
        handleError: (errors: ErrorMessage[]) => Promise<HealthInsuranceEligibility>,
        maxAttemptsCount: number
    ): Promise<HealthInsuranceEligibility> {
        for (let attempt = 1; attempt <= maxAttemptsCount; attempt++) {
            // If it's the first attempt we wait 4 seconds afterwards, retries are every 2 seconds
            const delayTime = attempt == 1 ? 4000 : 2000;

            const response = await sendRequest();

            if (!response.isSuccess)
                return await handleError(response.errors);

            if (response.data.Status !== HealthInsuranceEligibilityStatus.Pending)
                return await handleResponse(response);

            if (attempt == maxAttemptsCount) // ran out of attempts
                return new HealthInsuranceEligibility({
                    Eligibility: HealthInsuranceEligibilityStatus.Unknown
                });

            await this.delay(delayTime); // Delay before retrying
        }
    }

    private static delay(ms: number): Promise<void> {
        return new Promise(res => setTimeout(res, ms));
    }

    private static async handleResponse(response: ClientResponse<HealthInsuranceEligibilityResponse>): Promise<HealthInsuranceEligibility> {
        return new HealthInsuranceEligibility({
            Eligibility: response.data.Status,
            EligibilityExpiryDate: response.data.HealthCoverageExpiryDate,
            EligibilityMessage: response.data.Message
                .replaceAll("\t", "")
                .replaceAll("\n", "\r\n"),
            PatientDemographicInformation: HealthInsuranceEligibilityClient.mapPatientDemographicInformation(response)
        });
    }

    private static async handleError(errors: ErrorMessage[]): Promise<HealthInsuranceEligibility> {
        console.error(...errors);

        return new HealthInsuranceEligibility({
            Eligibility: HealthInsuranceEligibilityStatus.Unknown
        });
    }

    private static async sendEligibilityRequest(
        personalHealthNumber?: string,
        dateOfBirth?: DateOnly,
        patientHealthCardProvinceLookupId?: string
    ): Promise<ClientResponse<HealthInsuranceEligibilityResponse>> {
        return await Client.postAsync<HealthInsuranceEligibilityResponse>(
            ServerOperations.Server.Myle_ProvincialHealthInsurancePlan_GetEligibility,
            {
                PatientHealthCardNumber: personalHealthNumber,
                PatientDateOfBirth: dateOfBirth,
                PatientHealthCardProvinceLookupId: patientHealthCardProvinceLookupId
            }
        );
    }

    private static mapPatientDemographicInformation(response: ClientResponse<HealthInsuranceEligibilityResponse> | null): EligibilityPatientDemographicInformation {
        let demographicInformation: EligibilityPatientDemographicInformation | null = null;

        if (response?.data?.PatientDemographicInformation) {
            demographicInformation = new EligibilityPatientDemographicInformation({
                DateOfBirth: response.data.PatientDemographicInformation.DateOfBirth ? new Date(response.data.PatientDemographicInformation.DateOfBirth) : null,
                FirstName: response.data.PatientDemographicInformation.FirstName,
                Gender: response.data.PatientDemographicInformation.Gender,
                LastName: response.data.PatientDemographicInformation.LastName,
                MiddleName: response.data.PatientDemographicInformation.MiddleName
            });
        }

        return demographicInformation;
    }
}

export { HealthInsuranceEligibilityClient };
